import './styles.scss';
import classnames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@consumidor-positivo/aurora';
import { delay } from '@libs/utils/helpers/delay';

import { useCreditTrack } from '../../../../../hooks/useCreditTrack';

import cpClose from './images/close-cp.svg';
import iconWhatsapp from './images/iconWhatsapp.svg';
import iconFacebook from './images/iconFacebook.svg';
import iconLinkedin from './images/iconLinkedin.svg';
import iconTelegram from './images/iconTelegram.svg';
import iconX from './images/iconX.svg';
import iconCopy from './images/iconCopy.svg';

import { FacebookShareButton, LinkedinShareButton, TelegramShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';

export interface ShareProps {
  showShare: boolean;
  title?: string;
  message?: string;
  url?: string;
  onClose?: () => void;
}

export const Share: React.FC<ShareProps> = ({
  showShare,
  title = 'Compartilhar',
  message = 'Descubra seu Score, consulte dívidas e confira as melhores ofertas de crédito:',
  url = 'https://consumidorpositivo.com.br',
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const { elementClickedEvent, elementViewedEvent } = useCreditTrack();

  useEffect(() => {
    document.body.style.overflow = showShare ? 'hidden' : 'auto';
    if (showShare) {
      elementViewedEvent({
        elementType: 'modal',
        name: 'share',
        location: window.location.pathname,
        data: { message, url },
      });
    }
  }, [showShare]);

  function close(element: Element) {
    if (element.id === 'share') onClose && onClose();
  }

  async function share(social: 'whatsapp' | 'twitter' | 'facebook' | 'linkedin' | 'email' | 'telegram') {
    elementClickedEvent({
      elementType: 'button',
      name: 'share',
      location: window.location.pathname,
      text: social,
      data: { message, url },
    });
    onClose && onClose();
  }

  async function copy() {
    setCopied(true);
    elementClickedEvent({
      elementType: 'button',
      name: 'share',
      location: window.location.pathname,
      text: 'copiar',
      data: { message, url },
    });

    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
      } catch (err) {
        console.error(err);
      }
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error(err);
      }
      document.body.removeChild(textArea);
    }
    await delay(1000);
    setCopied(false);
    onClose && onClose();
  }

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (scrollContainerRef.current) {
      e.preventDefault();
      scrollContainerRef.current.scrollBy({
        left: e.deltaY,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      {createPortal(
        <div
          className={classnames('share', {
            'share-open': showShare,
            'share-close': !showShare,
          })}
          id="share"
          onClick={(e) => close(e.target as Element)}
        >
          <div
            className={classnames('share__card', {
              'slide-in-bottom': showShare,
              'slide-out-bottom': !showShare,
            })}
            id="card"
          >
            <div className="share__close">
              <img src={cpClose} alt="Icone de fechar" onClick={onClose}></img>
            </div>

            <h2 className="share__title">{title}</h2>
            <div className="share__content" ref={scrollContainerRef} onWheel={handleWheel}>
              <div className="share__item">
                <div className="share__item-content" onClick={copy}>
                  <img className={classnames({ share__copied: copied })} src={iconCopy} alt="Copiar" />
                  <h5 className="share__item-title">{copied ? 'Copiado ✓' : 'Copiar URL'}</h5>
                </div>
              </div>
              <div className="share__item">
                <WhatsappShareButton url={url} title={message} onClick={() => share('whatsapp')}>
                  <div className="share__item-content">
                    <img src={iconWhatsapp} alt="Whatsapp" />
                    <h5 className="share__item-title">WhatsApp</h5>
                  </div>
                </WhatsappShareButton>
              </div>
              <div className="share__item">
                <TwitterShareButton url={url} title={message} onClick={() => share('twitter')}>
                  <div className="share__item-content">
                    <img src={iconX} alt="X (Twitter)" />
                    <h5 className="share__item-title">X (Twitter)</h5>
                  </div>
                </TwitterShareButton>
              </div>
              <div className="share__item">
                <FacebookShareButton url={url} title={message} onClick={() => share('facebook')}>
                  <div className="share__item-content">
                    <img src={iconFacebook} alt="Facebook" />
                    <h5 className="share__item-title">Facebook</h5>
                  </div>
                </FacebookShareButton>
              </div>
              <div className="share__item">
                <LinkedinShareButton url={url} title={message} onClick={() => share('linkedin')}>
                  <div className="share__item-content">
                    <img src={iconLinkedin} alt="Linkedin" />
                    <h5 className="share__item-title">Linkedin</h5>
                  </div>
                </LinkedinShareButton>
              </div>
              <div className="share__item">
                <TelegramShareButton url={url} title={message} onClick={() => share('telegram')}>
                  <div className="share__item-content">
                    <img src={iconTelegram} alt="Telegram" />
                    <h5 className="share__item-title">Telegram</h5>
                  </div>
                </TelegramShareButton>
              </div>
            </div>
            <Button expand="x" type="outlined" onClick={onClose} size="medium">
              Voltar
            </Button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
